import React, { useEffect, useMemo, useRef, useState } from 'react';

type CountProps = { count?: number; className?: string };

type PetalProps = {
  count?: number;
  /** marigold | champagne | confetti */
  variant?: 'marigold' | 'champagne' | 'confetti';
  className?: string;
};

/** Falling marigold petals — Vedic / sacred only. */
export function FloatingPetals({ count = 18, variant = 'marigold', className = '' }: PetalProps) {
  const items = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${(i * 37 + 11) % 100}%`,
        delay: `${(i % 9) * 0.45}s`,
        duration: `${7 + (i % 5)}s`,
        size: 6 + (i % 5) * 2,
        rotate: (i * 40) % 360,
      })),
    [count],
  );

  const color =
    variant === 'champagne'
      ? 'rgba(232, 201, 106, 0.85)'
      : variant === 'confetti'
        ? undefined
        : 'rgba(240, 168, 32, 0.9)';

  return (
    <div className={`wow-petals ${className}`} aria-hidden>
      {items.map((p) => (
        <span
          key={p.id}
          className={`wow-petal wow-petal--${variant}`}
          style={{
            left: p.left,
            animationDelay: p.delay,
            animationDuration: p.duration,
            width: p.size,
            height: p.size * (variant === 'champagne' ? 1 : 1.4),
            background:
              variant === 'confetti'
                ? ['#e0c068', '#c0392b', '#f0a820', '#2d5a27', '#d9b65a'][p.id % 5]
                : color,
            transform: `rotate(${p.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
}

/** One-shot burst after scratch / seal open. */
export function ConfettiBurst({
  active,
  variant = 'confetti',
}: {
  active: boolean;
  variant?: PetalProps['variant'];
}) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) return;
    setShow(true);
    const t = window.setTimeout(() => setShow(false), 4200);
    return () => window.clearTimeout(t);
  }, [active]);
  if (!show) return null;
  return <FloatingPetals count={28} variant={variant} className="wow-petals--burst" />;
}

/** Ambient twinkle dots — prefer theme-specific effects over this when possible. */
export function SparkleField({ count = 24, className = '' }: CountProps) {
  const dots = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${(i * 53) % 100}%`,
        top: `${(i * 29) % 100}%`,
        delay: `${(i % 7) * 0.35}s`,
        size: 2 + (i % 3),
      })),
    [count],
  );
  return (
    <div className={`wow-sparkles ${className}`} aria-hidden>
      {dots.map((d) => (
        <span
          key={d.id}
          className="wow-spark"
          style={{
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            animationDelay: d.delay,
          }}
        />
      ))}
    </div>
  );
}

/** Diagonal foil light sweep — Emerald / luxury only. */
export function FoilShimmer({ className = '' }: { className?: string }) {
  return <div className={`wow-foil-shimmer ${className}`} aria-hidden />;
}

/** Floating diya embers — Vedic temple only. */
export function DiyaEmbers({ count = 12, className = '' }: CountProps) {
  const items = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${8 + ((i * 71) % 84)}%`,
        delay: `${(i % 6) * 0.55}s`,
        duration: `${5 + (i % 4)}s`,
        size: 4 + (i % 3) * 2,
      })),
    [count],
  );
  return (
    <div className={`wow-embers ${className}`} aria-hidden>
      {items.map((e) => (
        <span
          key={e.id}
          className="wow-ember"
          style={{
            left: e.left,
            width: e.size,
            height: e.size,
            animationDelay: e.delay,
            animationDuration: e.duration,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Vedic-only: soft incense smoke wisps rising from the lower third
 * (CSS, no WebGL — festive-ui / incense-smoke inspiration).
 */
export function IncenseSmoke({ count = 6, className = '' }: CountProps) {
  const wisps = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${18 + ((i * 13) % 64)}%`,
        delay: `${(i % 5) * 1.1}s`,
        duration: `${9 + (i % 4)}s`,
        width: 28 + (i % 3) * 14,
      })),
    [count],
  );
  return (
    <div className={`wow-incense ${className}`} aria-hidden>
      {wisps.map((w) => (
        <span
          key={w.id}
          className="wow-incense-wisp"
          style={{
            left: w.left,
            width: w.width,
            animationDelay: w.delay,
            animationDuration: w.duration,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Emerald-only: champagne bubbles rising (not falling petals).
 * Pattern: cinematiccard / sparkle.graphics bubbles.
 */
export function ChampagneBubbles({ count = 20, className = '' }: CountProps) {
  const bubbles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${(i * 41 + 7) % 100}%`,
        delay: `${(i % 8) * 0.55}s`,
        duration: `${6 + (i % 5)}s`,
        size: 4 + (i % 4) * 2,
      })),
    [count],
  );
  return (
    <div className={`wow-bubbles ${className}`} aria-hidden>
      {bubbles.map((b) => (
        <span
          key={b.id}
          className="wow-bubble"
          style={{
            left: b.left,
            width: b.size,
            height: b.size,
            animationDelay: b.delay,
            animationDuration: b.duration,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Emerald-only: foil specular that follows pointer (CSS vars).
 * Pattern: glassmorphism mouse-glow / WOW_VISION do-now.
 */
export function PointerFoilGlow({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;

    const onMove = (e: PointerEvent) => {
      const rect = parent.getBoundingClientRect();
      if (rect.width < 8 || rect.height < 8) return;
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--wow-px', `${x}%`);
      el.style.setProperty('--wow-py', `${y}%`);
    };

    parent.addEventListener('pointermove', onMove, { passive: true });
    return () => parent.removeEventListener('pointermove', onMove);
  }, []);

  return <div ref={ref} className={`wow-pointer-foil ${className}`} aria-hidden />;
}

/**
 * Scratch STD-only: blowing sand / dust haze (not marigold petals).
 * Pattern: sparkle.graphics sandstorm (lightweight CSS).
 */
export function SandDrift({ count = 24, className = '' }: CountProps) {
  const grains = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        top: `${(i * 17) % 100}%`,
        delay: `${(i % 10) * 0.35}s`,
        duration: `${4 + (i % 5)}s`,
        size: 2 + (i % 3),
      })),
    [count],
  );
  return (
    <div className={`wow-sand ${className}`} aria-hidden>
      {grains.map((g) => (
        <span
          key={g.id}
          className="wow-sand-grain"
          style={{
            top: g.top,
            width: g.size,
            height: g.size,
            animationDelay: g.delay,
            animationDuration: g.duration,
          }}
        />
      ))}
    </div>
  );
}

/** Scratch STD-only: gold-leaf flakes that burst once after reveal. */
export function GoldLeafBurst({ active, className = '' }: { active: boolean; className?: string }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) return;
    setShow(true);
    const t = window.setTimeout(() => setShow(false), 3800);
    return () => window.clearTimeout(t);
  }, [active]);

  const leaves = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        id: i,
        left: `${(i * 29 + 5) % 100}%`,
        delay: `${(i % 6) * 0.08}s`,
        rotate: (i * 37) % 360,
      })),
    [],
  );

  if (!show) return null;
  return (
    <div className={`wow-goldleaf ${className}`} aria-hidden>
      {leaves.map((l) => (
        <span
          key={l.id}
          className="wow-goldleaf-flake"
          style={{
            left: l.left,
            animationDelay: l.delay,
            transform: `rotate(${l.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
}
